<script setup lang="ts">
import { Play, Pause, Square } from 'lucide-vue-next'
import AirunTtsButton from './AirunTtsButton.vue'
import type { TtsLanguage } from '~/types'

interface Props {
  text: string
  label?: string
  slug?: string
  // Absent on older records, treated as Russian.
  language?: TtsLanguage
}

const props = defineProps<Props>()
const { t, locale } = useI18n()
const { speak, pause, stop, isPlaying, isPaused, currentText, isSupported } = useTTS()

const language = computed<TtsLanguage>(() => (props.language === 'ky' ? 'ky' : 'ru'))

// Exactly one provider is shown, picked here rather than by negating every
// other provider's condition in each template block:
//   ky → the TTS service, the only source of Kyrgyz audio
//   ru → the browser's own speech synthesis
const provider = computed<'airun' | 'browser' | null>(() => {
  if (language.value === 'ky') return props.slug ? 'airun' : null
  if (isSupported.value) return 'browser'
  return null
})

// Only browser playback is driven from here: AirunTtsButton stops itself on
// locale change and unmount.
watch(locale, stop)

// Cleanup on unmount
onBeforeUnmount(stop)

// Stop playback on full page navigation (e.g. external locale switch)
onMounted(() => {
  window.addEventListener('beforeunload', stop)
})
onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', stop)
})

const displayLabel = computed(() => props.label || t('ui.tts_controls.listen_to_article'))

const togglePlay = () => {
  if (isPlaying.value) {
    pause()
  } else if (isPaused.value) {
    speak(currentText.value)
  } else {
    speak(props.text)
  }
}

const handleStop = () => {
  stop()
}
</script>

<template>
  <ClientOnly>
    <div
      v-if="provider === 'browser'"
      class="group relative flex w-[360px] max-w-full items-center gap-3 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 shadow-sm transition-all duration-300 hover:shadow-md dark:from-blue-950/30 dark:to-indigo-950/30"
    >
      <div class="flex min-w-0 items-center gap-2">
        <div
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white shadow-sm dark:bg-gray-800"
        >
          <svg
            class="h-5 w-5 text-blue-600 dark:text-blue-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
            />
          </svg>
        </div>
        <div class="flex min-w-0 flex-col">
          <span class="truncate text-sm font-semibold text-gray-900 dark:text-white">
            {{ displayLabel }}
          </span>
        </div>
      </div>

      <div class="ml-auto flex shrink-0 items-center gap-2">
        <button
          class="group/btn relative flex h-10 w-10 items-center justify-center rounded-full transition-all duration-200"
          :class="[
            isPlaying
              ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/50 hover:bg-blue-700 hover:shadow-xl hover:shadow-blue-500/60'
              : 'bg-white text-blue-600 shadow-md hover:bg-blue-50 hover:shadow-lg dark:bg-gray-800 dark:text-blue-400 dark:hover:bg-gray-700'
          ]"
          :title="
            isPlaying ? $t('ui.tts_controls.pause_reading') : $t('ui.tts_controls.play_reading')
          "
          @click="togglePlay"
        >
          <Play v-if="!isPlaying" class="h-5 w-5 transition-transform group-hover/btn:scale-110" />
          <Pause v-else class="h-5 w-5 transition-transform group-hover/btn:scale-110" />
        </button>

        <button
          v-if="isPlaying || isPaused"
          class="group/btn flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-600 shadow-md transition-all duration-200 hover:bg-red-50 hover:text-red-600 hover:shadow-lg dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-red-950/30 dark:hover:text-red-400"
          :title="$t('ui.tts_controls.stop_reading')"
          @click="handleStop"
        >
          <Square class="h-4 w-4 transition-transform group-hover/btn:scale-110" />
        </button>
      </div>
    </div>

    <AirunTtsButton
      v-if="provider === 'airun'"
      :text="props.text"
      :slug="props.slug!"
      :language="language"
    />
  </ClientOnly>
</template>
